import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
    id: number;
    question: string;
    answer: string;
}

interface FAQProps {
    title?: string;
    subtitle?: string;
    faqs: FAQItem[];
    className?: string;
}

const FAQ: React.FC<FAQProps> = ({ 
    title = "Frequently Asked Questions", 
    subtitle = "Find answers to common questions about our platform",
    faqs,
    className = ""
}) => {
    const [openItems, setOpenItems] = useState<number[]>([]);

    const toggleItem = (id: number) => {
        setOpenItems(prev => 
            prev.includes(id) 
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    return (
        <section className={`py-16 bg-gradient-to-br from-gray-50 to-white ${className}`}>
            <div className="container mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        {title}
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        {subtitle}
                    </p>
                </div>

                {/* FAQ Items */}
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-4">
                        {faqs.map((faq) => {
                            const isOpen = openItems.includes(faq.id);
                            return (
                                <div
                                    key={faq.id}
                                    className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                                >
                                    <button
                                        onClick={() => toggleItem(faq.id)}
                                        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                            {faq.question}
                                        </h3>
                                        <div className="flex-shrink-0">
                                            {isOpen ? (
                                                <ChevronUp className="w-5 h-5 text-blue-600" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>
                                    </button>
                                    
                                    {isOpen && (
                                        <div className="px-6 pb-5 border-t border-gray-100">
                                            <div className="pt-4">
                                                <p className="text-gray-700 leading-relaxed">
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Contact CTA */}
                <div className="text-center mt-12">
                    <p className="text-gray-600 mb-4">
                        Still have questions? We're here to help!
                    </p>
                    <a
                        href="/contact"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                    >
                        Contact Support
                    </a>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
